import clsx from 'clsx'
import Router from 'next/router'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import Loader from 'components/loader'
import useSearch from 'components/shared/useSWR/useSearch'
import Typo from 'constants/typography'
import {
  segmentTrackProductListViewed,
  segmentTrackSearchProducts,
} from 'lib/segment'
import {
  SEGMENT_STORAGE_KEY_SEARCH_ID,
  SEGMENT_STORAGE_KEY_SEARCH_IDX,
} from 'lib/segment/const'
import { getPreferredSizes } from 'lib/size-preference'
import Tracking from 'lib/tracking'
import CategoryProductList from 'pages/category/list'
import { SEARCH_SORT_OPTIONS } from 'pages/search/const'
import { ITEMS_LIMIT } from '../const'
import EmptySearchResult from '../no-result'
import SearchRequestError from '../request-error'
import {
  filterRelatedProducts,
  getIntentExpansion,
  getSearchPageUrls,
  getSearchResultParams,
  isLikelyRealQuery,
  rankSearchResults,
} from '../utils'
import styles from './style.scss'

const DEFAULT_SORT = 'popular'

const messages = {
  // ASOS wording; misspellings are shown as typed, with the right products
  results: ['Your search results for:', 'ผลการค้นหาสำหรับ:'],
  // "291 results for" — count-first, the common e-commerce pattern
  resultsFor: ['results for', 'รายการสำหรับ'],
  result: ['result for', 'รายการสำหรับ'],
  nothingTitle: ['Nothing matches your search', 'ไม่พบสินค้าที่ตรงกับการค้นหา'],
  nothingBody: [
    "But don't give up – check the spelling or try less specific search terms.",
    'อย่าเพิ่งยอมแพ้ ลองตรวจสอบตัวสะกด หรือใช้คำค้นหาที่กว้างขึ้น',
  ],
  sort: ['Sort by', 'เรียงตาม'],
  seeAll: ['See all & filter', 'ดูทั้งหมดและตัวกรอง'],
}

const copy = (language, key) => messages[key][language === 'th' ? 1 : 0]

// Same events as the full search page (pages/search track()).
export const trackInPageSearch = ({ page, query, results, sort }) => {
  const {
    products = [],
    totalHits = 0,
    queryID = '',
    index = '',
  } = results || {}

  if (typeof window !== 'undefined') {
    sessionStorage.setItem(SEGMENT_STORAGE_KEY_SEARCH_ID, queryID)
    sessionStorage.setItem(SEGMENT_STORAGE_KEY_SEARCH_IDX, index)
  }

  segmentTrackSearchProducts({
    active_filters: [],
    page_number: page,
    productIds: products.map((hit) => hit.id_product),
    query,
    sort_by: sort,
    query_id: queryID,
    query_idx: index,
  })
  if (!products.length) {
    segmentTrackProductListViewed(
      { page_number: page, product_list_ids: [] },
      { ref_type: 'zero_search_result', ref_id: '' },
    )
  }

  Tracking.trackEvent(Tracking.EVENT_NAME_SEARCH, {
    'rtb-search': products.slice(0, 4).map((item) => item.id_product),
    'search-term': query,
    'search-results-count': totalHits,
  })
}

// Loading stops after this many pages in a row with nothing related (the
// API's padded tail); one is not enough when sorting by price mixes it in.
const MAX_UNRELATED_PAGES = 2

// When a real query has fewer strict (all-words) matches than this, widen to
// the API's full result set and rank it specific -> hybrid, so thin/mixed
// queries ("top hat", "beach wedding outfit") lead with the closest matches
// instead of a raw jumble or "Nothing matches". Queries with plenty of strict
// matches ("red dress") are never widened. Tunable: raise to top up more thin
// queries with partial matches, lower to stay stricter. The robust version is
// server-side (synonyms + removeWordsIfNoResults; see the strategy doc).
const MIN_STRICT_RESULTS = 4

const ResultsList = ({ isPhone, language, query, sort }) => {
  const [page, setPage] = useState(0)
  const [loadedPage, setLoadedPage] = useState(-1)
  const [rawProducts, setRawProducts] = useState([])
  const [unrelatedPages, setUnrelatedPages] = useState(0)
  const [apiExhausted, setApiExhausted] = useState(false)
  const sentinelRef = useRef(null)

  const { data, error, retry, isValidating } = useSearch(
    getSearchResultParams([], page, query, sort),
    null,
  )
  // Total matches for the query, shown in the header ("291 results for …").
  const totalHits = data?.totalHits || 0

  // Shown products: the accumulated results filtered to real matches. Filtering
  // the whole set at once (not page by page) keeps the typo-tolerance decision
  // stable as more pages load.
  const products = useMemo(
    () => filterRelatedProducts(rawProducts, query),
    [rawProducts, query],
  )

  // An emotional / seasonal / occasion phrase ("date night") has no literal
  // product matches, so its results come entirely from the widened semantic
  // tail - the pagination/exhaustion logic below keys off this too.
  const intentTerms = useMemo(() => getIntentExpansion(query), [query])

  // What to show: explicit keyword matches when we have them; otherwise, for a
  // real query, fall back to the API's semantic results (the "tail") instead of
  // showing nothing. Available items first for the default relevance sort;
  // explicit New In / Price sorts keep the API order.
  const displayProducts = useMemo(() => {
    // Enough strict matches -> show only those (precise). Too few, for a real
    // query -> widen to everything the API returned and rank specific->hybrid.
    const widen =
      (Boolean(intentTerms) || products.length < MIN_STRICT_RESULTS) &&
      isLikelyRealQuery(query) &&
      rawProducts.length > 0
    const baseList = widen ? rawProducts : products
    if (sort !== DEFAULT_SORT) {
      return baseList
    }
    // Default relevance sort, size-aware: common-size availability first, then
    // relevance (intent terms or keyword specificity), then the shopper's own
    // size (personalization), then in-stock. See rankSearchResults.
    return rankSearchResults(baseList, {
      query,
      intentTerms,
      preferredSizes: getPreferredSizes(),
    })
  }, [products, rawProducts, query, sort, intentTerms])

  useEffect(() => {
    if (!data?.products || loadedPage === page) {
      return
    }

    const nextRaw =
      page === 0 ? data.products : [...rawProducts, ...data.products]
    const addedRelated =
      filterRelatedProducts(nextRaw, query).length -
      (page === 0 ? 0 : products.length)
    const pageRelated = filterRelatedProducts(data.products, query)

    setRawProducts(nextRaw)
    setUnrelatedPages((prev) => (addedRelated > 0 ? 0 : prev + 1))
    setApiExhausted(data.products.length < ITEMS_LIMIT)
    setLoadedPage(page)
    trackInPageSearch({
      page: page + 1,
      query,
      results: {
        ...data,
        // track the semantic tail as results, not a zero-result, for a real query
        products:
          pageRelated.length || !isLikelyRealQuery(query)
            ? pageRelated
            : data.products,
      },
      sort,
    })
  }, [data, loadedPage, page, products, query, rawProducts, sort])

  // "No results" and exhaustion follow what's actually shown (displayProducts),
  // not the strict keyword set - an intent query ("date night") has zero strict
  // matches but a full semantic tail, so keying off `products` here would kill
  // infinite scroll at page 0. The unrelated-pages cap is a strict-keyword guard,
  // so it doesn't apply to intent queries (their tail is the wanted result).
  const noResults = loadedPage >= 0 && displayProducts.length === 0
  const isExhausted =
    apiExhausted ||
    (!intentTerms && unrelatedPages >= MAX_UNRELATED_PAGES) ||
    (noResults && page === 0)

  const isLoadingMore = loadedPage !== page
  const canLoadMore =
    loadedPage >= 0 && !isExhausted && !isLoadingMore && !error

  // Load the next page as the shopper nears the end of the list (no button).
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (
      !canLoadMore ||
      !sentinel ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPage(loadedPage + 1)
        }
      },
      { rootMargin: '0px 0px 600px 0px' },
    )
    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [canLoadMore, loadedPage])

  if (error && !displayProducts.length) {
    return <SearchRequestError retry={retry} isValidating={isValidating} />
  }

  if (loadedPage < 0) {
    return (
      <div className="in-page-search__loader">
        <Loader />
      </div>
    )
  }

  if (!displayProducts.length && isExhausted) {
    return (
      <EmptySearchResult
        query={query}
        message={
          <div className="in-page-search__nothing" role="status">
            <h2 className={Typo.h6}>{copy(language, 'nothingTitle')}</h2>
            <p className={Typo.body2}>{copy(language, 'nothingBody')}</p>
          </div>
        }
      />
    )
  }

  return (
    <React.Fragment>
      <p className={clsx(Typo.caption, 'in-page-search__count')} role="status">
        <strong className={Typo.h6}>
          {(totalHits || displayProducts.length).toLocaleString()}
        </strong>{' '}
        {copy(language, totalHits === 1 ? 'result' : 'resultsFor')}{' '}
        <strong className={Typo.h6}>{`"${query}"`}</strong>
      </p>
      <CategoryProductList
        displayHoverInteractions
        isSearchPage
        isPhone={isPhone}
        list={displayProducts}
        refId={query}
        refType="search"
      />
      {error && (
        <SearchRequestError retry={retry} isValidating={isValidating} />
      )}
      {!isExhausted && (
        <div
          ref={sentinelRef}
          className="in-page-search__more"
          data-cy="in-page-search-more"
        >
          {isLoadingMore && <Loader />}
        </div>
      )}
    </React.Fragment>
  )
}

ResultsList.propTypes = {
  isPhone: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  query: PropTypes.string.isRequired,
  sort: PropTypes.string.isRequired,
}

export const InPageSearchResults = ({ isPhone, language, query }) => {
  const [sort, setSort] = useState(DEFAULT_SORT)

  // The full search page keeps filters, so "see all" hands over to it.
  const openFullSearchPage = () => {
    const { href, as } = getSearchPageUrls(query)
    Router.replace(href, as)
  }

  return (
    <div className="in-page-search" data-cy="in-page-search">
      <style jsx>{styles}</style>
      <div className="in-page-search__bar">
        <div className={clsx(Typo.caption, 'in-page-search__sort')}>
          <label htmlFor="in-page-search-sort">{copy(language, 'sort')}</label>
          <select
            id="in-page-search-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value || DEFAULT_SORT)}
          >
            {SEARCH_SORT_OPTIONS.map(({ key, label }) => (
              <option key={key} value={key || DEFAULT_SORT}>
                {label[language] || label.en}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className={clsx(Typo.caption, 'in-page-search__see-all')}
          onClick={openFullSearchPage}
        >
          {copy(language, 'seeAll')}
        </button>
      </div>
      <ResultsList
        key={sort}
        isPhone={isPhone}
        language={language}
        query={query}
        sort={sort}
      />
    </div>
  )
}

InPageSearchResults.propTypes = {
  isPhone: PropTypes.bool,
  language: PropTypes.string,
  query: PropTypes.string.isRequired,
}

InPageSearchResults.defaultProps = {
  isPhone: false,
  language: 'en',
}

export default connect((state) => ({
  isPhone: state.device.isPhone,
  language: state.internationalization.language,
}))(InPageSearchResults)
